import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApiFragment,
    ApiRecordType,
    Family,
    FieldReadHelper,
    RecordStatus,
    RecordTypesService,
    UserFunctions,
    UserService
} from '@stylehaven/components';
import { MiddlewareService } from './middleware.abstract.service';

@Injectable({ providedIn: 'root' })
export class ContextMiddleware implements MiddlewareService {
    private readonly recordTypeService: RecordTypesService = inject(RecordTypesService);
    constructor(
        private readonly userService: UserService,
        protected fieldHelper: FieldReadHelper
    ) {}

    public annotate(apiFragment: ApiFragment): Observable<ApiFragment> {
        return this.appendValidationToContext(apiFragment).pipe(
            map(record => this.appendAllowedChildrenToContext(record))
        );
    }

    private appendValidationToContext(apiFragment: ApiFragment): Observable<ApiFragment> {
        return this.userService.getUserFunctions().pipe(
            map(userFunctions => {
                apiFragment = this.setActionableStatuses(apiFragment, userFunctions);
                apiFragment = this.setSubmitRights(apiFragment, userFunctions);

                return apiFragment;
            })
        );
    }

    private setActionableStatuses(apiFragment: ApiFragment, userFunctions: UserFunctions): ApiFragment {
        const isReviewer: boolean = userFunctions.EVALUATE_MEDIA;

        const baseEditableStatuses: RecordStatus[] = [
            RecordStatus.DraftValid,
            RecordStatus.RejectedForCorrection,
            RecordStatus.Published,
            RecordStatus.Archived
        ];
        const editableStatuses: RecordStatus[] = [...baseEditableStatuses, RecordStatus.DraftInvalid, RecordStatus.New];
        const submittableStatuses: RecordStatus[] = [...baseEditableStatuses];

        if (isReviewer) {
            editableStatuses.push(RecordStatus.Submitted);
        }

        const hasEditableStatus: boolean = editableStatuses.includes(apiFragment.getRecordStatus());
        const hasSubmittableStatus: boolean = submittableStatuses.includes(apiFragment.getRecordStatus());

        apiFragment.set(Family.Context, 'HasEditableStatus', hasEditableStatus);
        apiFragment.set(Family.Context, 'HasSubmittableStatus', hasSubmittableStatus);
        return apiFragment;
    }

    /*   Submittable when:
     *   * It is not yet published
     *   * It is editable (provided by original IsEditable context value)
     *   * User has SUBMIT_MEDIA userfunction
     */
    private setSubmitRights(apiFragment: ApiFragment, userFunctions: UserFunctions): ApiFragment {
        const isSubmittable: boolean =
            apiFragment.hasEditRights() && !apiFragment.isPublished() && userFunctions.SUBMIT_MEDIA;

        apiFragment.set(Family.Context, 'IsSubmittable', isSubmittable);
        return apiFragment;
    }

    private appendAllowedChildrenToContext(record: ApiFragment): ApiFragment {
        const allowedChildren: ApiRecordType[] = this.recordTypeService.getAllowedChildren(record.getRecordType());

        record.set(Family.Context, 'AllowedChildren', allowedChildren);

        return record;
    }
}
